// src/components/RoomList.js
import React, {useMemo, useState} from 'react';
import styled, {css} from 'styled-components';
import {useChat} from '../context/ChatProvider';
import useDebounce from '../hooks/useDebounce';
import {Description} from '../styled/Description';
import {useTranslation} from 'react-i18next';
import {AiOutlineArrowRight} from "react-icons/ai";

// Styled Components

const RoomListContainer = styled.div`
    --space: 1em;
    --horizontal-space: 1.5em;
    --vertical-padding: 1em;

    display: flex;
    flex-direction: column;
    width: 26%;
    height: 100%;
    padding-top: var(--vertical-padding);
    overflow: auto;
    border-top-left-radius: 45px;
    border-bottom-left-radius: 45px;
    background: var(--blue-gradient);
    color: #fff;

    & h3 {
        font-size: 1.2em;
        font-weight: 500;
        padding: 0.9em var(--horizontal-space);
        margin: 0;
    }

    @media (max-width: 820px) {
        position: absolute;
        opacity: ${(props) => (props.open ? '1' : '0')};
        pointer-events: ${(props) => (props.open ? 'auto' : 'none')};
        right: 0;
        width: 100%;
        border-radius: 0;
        z-index: 1;
        transition: opacity 0.3s ease-in-out;
    }
`;

const RoomItem = styled.li`
    display: flex;
    gap: 1vw;
    width: 100%;
    flex: 1;
    padding: var(--space) var(--horizontal-space);
    list-style: none;
    background: ${(props) => props.active ? 'var(--blue-active-color)' : 'transparent'};
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background: var(--blue-active-color);
    }

    & img {
        height: 3vw;
        width: 3vw;
        border-radius: 50%;
        object-fit: cover;
    }

    & div {
        display: flex;
        flex-direction: column;
        justify-content: center;
    }

    & span {
        font-weight: 500;
        font-size: 0.9em;
    }
`;

// Shared Styles for Input and Select Elements
const sharedInputStyles = css`
    width: 100%;
    padding: 10px 15px;
    border: 2px solid #ddd;
    border-radius: 8px;
    font-size: 1em;
    transition: border-color 0.3s;

    &::placeholder {
        color: #7b7b7b;
        font-size: 1em;
    }

    &:focus {
        outline: none;
        border-color: var(--blue-active-color);
    }

    @media (max-width: 820px) {
        font-size: 0.9em;
    }
`;

// Styled Select Component
const Select = styled.select`
    ${sharedInputStyles}
    background: #fff;
    color: #000;
    cursor: pointer;

    /* Optional: Customize the select arrow for consistency */
    appearance: none;
    background-image: url("data:image/svg+xml;charset=US-ASCII,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10'><path fill='%23000' d='M0 0l5 5 5-5z'/></svg>");
    background-repeat: no-repeat;
    background-position: right 10px center;
    background-size: 10px;
    padding-right: 30px; /* Ensure space for the custom arrow */
`;

// Styled Language Selector Wrapper
const LanguageSelectorWrapper = styled.div`
    display: flex;
    flex-direction: column; /* Align items vertically */
    gap: 0.5vw; /* Adjusted gap for better spacing */
    width: 100%;
    padding: var(--space) var(--horizontal-space);
    background: transparent;
    transition: all 0.2s;

    &:hover {
        background: var(--blue-active-color);
    }

    & label {
        font-weight: 500;
        font-size: 0.9em;
        color: #fff;
    }
`;

// Styled User Info Section
const UserInfo = styled.div`
    text-align: left; /* Ensure left alignment */
    padding: var(--space) var(--horizontal-space);
    background: var(--blue-color);
    border-radius: 8px;

    & p {
        margin: 0;
        font-weight: 500;
        font-size: 0.9em;
    }

    & h3 {
        margin-bottom: 0.5em; /* Space between heading and button */
    }
`;

// Styled Submit Button
const SubmitButton = styled.button`
    margin-top: 1em;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px 1em;
    border: none;
    border-radius: 8px;
    background: var(--blue-active-color);
    transition: 0.3s ease-in-out opacity, box-shadow;
    cursor: pointer;
    font-size: 1em;
    color: #fff;

    &:hover {
        opacity: 0.85;
        box-shadow: 0 8px 15px rgba(0, 0, 0, 0.3);
    }

    &:focus {
        outline: none;
    }

    @media (max-width: 820px) {
        font-size: 0.9em;
    }
`;

const Logo = styled.img`
    margin-left: 1vw;
    width: 250px;
    height: auto;

    @media (max-width: 820px) {
        margin-left: 2vw;
        width: 250px;
    }
`;

const ContactInfo = styled.div`
    margin-top: 1.5em;
    font-size: 0.7em;
    margin-left: 2.5em;
    margin-right: 2vw;
    color: rgba(255, 255, 255, 0.6);
    text-align: left;

    @media (max-width: 820px) {
        font-size: 0.7em;
        margin-left: 2em;
    }

    & a {
        color: rgba(255, 255, 255, 0.8);
        text-decoration: underline;
        transition: color 0.2s;

        &:hover {
            color: rgba(255, 255, 255, 1);
        }
    }

    & .social-icons {
        margin-top: 0.5em;
        display: flex;
        justify-content: center;
        gap: 1em;

        & a {
            color: rgba(255, 255, 255, 0.8);
            font-size: 1.2em;

            &:hover {
                color: rgba(255, 255, 255, 1);
            }
        }
    }
`;

const RoomList = ({query, isNavOpen, setIsNavOpen, toggleLogView}) => {
    const {currentRoom, setCurrentRoom, language, setLanguage, username, handleLogout} = useChat();
    const {t, i18n} = useTranslation();
    const debouncedSearch = useDebounce(query, 350);

    const [isLogView, setIsLogView] = useState(false); // New state to toggle log view

    const rooms = useMemo(() => {
        const roomData = [
            {
                id: process.env.REACT_APP_ROOM_3_ID,
                name: t('rooms.room2.name'),
                src: process.env.REACT_APP_ROOM_3_SRC,
                description: t('rooms.room2.description'),
                api: process.env.REACT_APP_ROOM_3_API,
                current_thread_id: null,
            }
        ];

        return roomData.map(room => ({
            ...room,
            id: parseInt(room.id, 10) // Convert id to number
        }));
    }, [t]);

    const filteredRooms = useMemo(() => {
        if (!debouncedSearch) return rooms;

        return rooms.filter((room) => {
            const lowerName = room.name.toLowerCase();
            const lowerDesc = room.description.toLowerCase();
            const search = debouncedSearch.toLowerCase();
            return lowerName.includes(search) || lowerDesc.includes(search);
        });
    }, [debouncedSearch, rooms]);

    const handleRoomClick = (roomID) => {
        if (currentRoom?.id === roomID) return;

        const selectedRoom = rooms.find((room) => room.id === roomID);
        setCurrentRoom(selectedRoom);
        setIsNavOpen(false);
    };

    const handleLanguageChange = (e) => {
        const newLang = e.target.value;
        setLanguage(newLang);
        i18n.changeLanguage(newLang);
    };

    const handleViewLogs = () => {
        setIsLogView(true); // Show logs view
        toggleLogView();
    };

    const handleBackToChat = () => {
        setIsLogView(false); // Hide logs view
        toggleLogView();
        setIsNavOpen(true);
    };

    return (
        <RoomListContainer open={isNavOpen}>
            <Logo src={'./images/logo.svg'} alt="BITFLINX Logo"/>

            {isLogView ? (
                <UserInfo>
                    <SubmitButton onClick={handleBackToChat}>
                        {t('log.back') || 'Get back to chat'}
                    </SubmitButton>
                </UserInfo>
            ) : (
                <>
                    <h3>{t('language.title') || 'Language'}</h3>

                    <LanguageSelectorWrapper>
                        <label htmlFor="language-select">{t('language.select') || 'Select Language'}</label>
                        <Select
                            id="language-select"
                            value={language}
                            onChange={handleLanguageChange}
                            aria-label={t('language.select') || "Select Language"}
                        >
                            <option value="en">{t('language.english') || 'English'}</option>
                            <option value="de">{t('language.german') || 'Deutsch'}</option>
                            <option value="ro">{t('language.romanian') || 'Romanian'}</option>
                            <option value="ar">{t('language.arabic') || 'Arabic'}</option>
                        </Select>
                        <Description color="rgba(254,254,254,0.5)" size="0.7em">
                            {t('language.explanation') || 'Select your preferred language.'}
                        </Description>
                    </LanguageSelectorWrapper>

                    <h3>{t('rooms.title') || 'Rooms'}</h3>

                    <ul>
                        {filteredRooms.map((room) => {
                            const {id, name, src, description} = room;

                            return (
                                <RoomItem
                                    active={currentRoom?.id === id}
                                    key={id}
                                    onClick={() => handleRoomClick(id)}
                                >
                                    <img alt={`${name} avatar`} src={src}/>
                                    <div>
                                        <span>{name}</span>
                                        <Description color="rgba(254,254,254,0.5)" size="0.7em">
                                            {description}
                                        </Description>
                                    </div>
                                </RoomItem>
                            );
                        })}
                    </ul>

                    {username && ( // Conditionally render username and logout
                        <UserInfo>
                            {t('user.loggedInAs') || 'Logged in as:'} {username}
                            <SubmitButton variant="logout" onClick={handleLogout}
                                          aria-label={t('user.logout') || 'Logout'}>
                                {t('user.logout') || 'Logout'}
                            </SubmitButton>
                        </UserInfo>
                    )}

                    <ContactInfo>
                        <p>{t('impressum.message') || 'Contact us:'} <a
                            href="mailto:open@bitflinx.com">open@bitflinx.com</a></p>
                    </ContactInfo>

                    <UserInfo>
                        <SubmitButton onClick={handleViewLogs}>
                            {t('log.view') || 'View logs'}
                        </SubmitButton>
                    </UserInfo>
                </>
            )}
        </RoomListContainer>
    );
};

export default RoomList;
